import { useMemo, useState } from 'react';
import { useData } from '../AdoptionData.jsx';
import {
  aggregate, distinctPrompts, histograms, avgWowRetention,
  companyOf, sanitizeLabels, SCIENTIFIC_AREAS, USE_CASES,
} from '../../../../lib/adoptionAnalytics.js';
import { sum, AREA_COLOR, USE_COLOR } from '../util.js';
import { LoyaltyChart, UsageChart, HBars, Heatmap } from '../charts.jsx';
import { Kpi, Kpis } from '../Kpi.jsx';
import AdoptionReport from './AdoptionReport.jsx';

export default function CompanyDetail() {
  const { events, aliases, classMap, selectedCompany, setSelectedCompany } = useData();
  const [mode, setMode] = useState('user');
  const [minQ, setMinQ] = useState(0);

  const company = selectedCompany;

  const subEvents = useMemo(
    () => events.filter((e) => companyOf(e.user, aliases) === company),
    [events, aliases, company]
  );
  const agg = useMemo(() => aggregate(subEvents, { aliases }), [subEvents, aliases]);
  const usageAgg = useMemo(() => aggregate(subEvents, { aliases, minQueriesPerDay: minQ }), [subEvents, aliases, minQ]);
  const prompts = useMemo(() => distinctPrompts(subEvents, aliases), [subEvents, aliases]);

  const hist = useMemo(() => {
    const classified = prompts.map((p) => {
      const c = classMap[p.text] || {};
      return {
        company: p.company,
        areas: sanitizeLabels(c.areas, SCIENTIFIC_AREAS, 'Unspecified/General'),
        uses: sanitizeLabels(c.uses, USE_CASES, 'Other'),
      };
    });
    return histograms(classified);
  }, [prompts, classMap]);
  const hasClass = prompts.some((p) => classMap[p.text]);

  const areaOrder = useMemo(() => [...SCIENTIFIC_AREAS].sort((a, b) => (hist.aggArea[b] || 0) - (hist.aggArea[a] || 0)), [hist]);
  const useOrder = useMemo(() => [...USE_CASES].sort((a, b) => (hist.aggUse[b] || 0) - (hist.aggUse[a] || 0)), [hist]);

  const usersByVol = useMemo(
    () => agg.users.map((u) => ({ user: u, ...agg.perUser[u] })).sort((a, b) => b.events - a.events),
    [agg]
  );

  if (!company) return <div className="status">Select a company. <button type="button" className="linklike" onClick={() => setSelectedCompany(null)}>Back to companies</button></div>;
  if (!agg.weeks.length) return <div className="status">No prompts for {company}.</div>;

  return (
    <>
      <div className="crumb"><button type="button" className="linklike" onClick={() => setSelectedCompany(null)}>← Companies</button></div>

      <AdoptionReport events={subEvents} aliases={aliases} scope={company} />

      <h2 className="section-rule">Detailed usage &amp; loyalty</h2>
      <Kpis>
        <Kpi v={prompts.length} l="Distinct prompts" />
        <Kpi v={agg.users.length} l="Users" />
        <Kpi v={sum(agg.totalUserDays)} l="Active user-days" />
        <Kpi v={agg.compUserDays[company]?.filter((v) => v > 0).length || 0} l="Active weeks" />
        <Kpi v={`${Math.round(avgWowRetention(agg) * 100)}%`} l="Avg WoW retention" />
        <Kpi v={`${agg.dateRange[0]} → ${agg.dateRange[1]}`} l="Date range" small />
      </Kpis>

      <section className="card">
        <h2>Loyalty — New vs Returning Users</h2>
        <LoyaltyChart agg={agg} />
        <div className="legend">
          <span className="leg"><span className="sw" style={{ background: '#5b8def' }} />Returning</span>
          <span className="leg"><span className="sw" style={{ background: '#f5a623' }} />New</span>
          <span className="leg"><span className="sw" style={{ background: '#37c98b' }} />WoW retention %</span>
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h2>Usage — Weekly Active Days</h2>
          <div className="card-controls">
            <label className="thresh">Count days with &gt;
              <input type="number" min="0" max="50" value={minQ}
                onChange={(e) => setMinQ(Math.max(0, parseInt(e.target.value, 10) || 0))} /> queries
            </label>
            <div className="toggle">
              <button className={mode === 'user' ? 'on' : ''} onClick={() => setMode('user')}>By user</button>
              <button className={mode === 'total' ? 'on' : ''} onClick={() => setMode('total')}>Total trend</button>
            </div>
          </div>
        </div>
        <p className="desc">Active day = a day with &gt; {minQ} {minQ === 1 ? 'query' : 'queries'}.</p>
        <UsageChart agg={usageAgg} mode={mode} />
      </section>

      {hasClass && (
        <section className="card">
          <h2>Prompt categories</h2>
          <p className="desc">Multi-label — bars sum to more than the {prompts.length} prompts.</p>
          <div className="grid2">
            <div><h3>Scientific area</h3><HBars counts={hist.aggArea} order={areaOrder} color={AREA_COLOR} denom={prompts.length} /></div>
            <div><h3>Use case</h3><HBars counts={hist.aggUse} order={useOrder} color={USE_COLOR} denom={prompts.length} /></div>
          </div>
        </section>
      )}

      <section className="card">
        <h2>Per-user activity heatmap</h2>
        <Heatmap agg={agg} users={usersByVol.map((u) => u.user)} />
        <div className="legend">Active days/week:
          <span className="leg"><span className="sw" style={{ background: '#2f5a8a' }} />1</span>
          <span className="leg"><span className="sw" style={{ background: '#4d86c4' }} />2</span>
          <span className="leg"><span className="sw" style={{ background: '#73b0e6' }} />3</span>
          <span className="leg"><span className="sw" style={{ background: '#a9d2f5' }} />4+</span>
        </div>
      </section>

      <section className="card">
        <h2>End-user summary</h2>
        <div className="table-wrap">
          <table className="tbl">
            <thead><tr><th>User</th><th className="r">Prompts</th><th className="r">Active weeks</th><th className="r">Active days</th><th>First</th><th>Last</th></tr></thead>
            <tbody>
              {usersByVol.map((u) => (
                <tr key={u.user}><td>{u.user}</td><td className="r">{u.events}</td><td className="r">{u.activeWeeks}</td><td className="r">{u.activeDays}</td><td>{u.first || '—'}</td><td>{u.last || '—'}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
